import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { NotesModal, fmtDate, fmtTime } from '../approvals/shared';

const API = `${window.__BACKEND_URL__}/api`;

const STATUS_STYLES = {
  pending:  { label: '⏳ Pending',  bg: 'rgba(255,200,0,0.15)',  color: '#FACC15' },
  approved: { label: '✅ Approved', bg: 'rgba(0,200,150,0.15)',  color: '#00C896' },
  rejected: { label: '❌ Rejected', bg: 'rgba(239,68,68,0.15)',  color: '#F87171' },
  applied:  { label: '📤 Applied',  bg: 'rgba(30,136,229,0.15)', color: '#60A5FA' },
};

const NewAdjustmentModal = ({ token, onDone, onClose }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState({ entry_id: '', hours: '', reason: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payroll/adjustments/locked-entries`, auth)
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
  }, [token]); // eslint-disable-line

  const submit = async () => {
    const hours = parseFloat(form.hours);
    if (!form.entry_id) { toast({ title: 'Select a locked entry', variant: 'destructive' }); return; }
    if (isNaN(hours) || hours < 0) { toast({ title: 'Enter the corrected hours', variant: 'destructive' }); return; }
    if (form.reason.trim().length < 10) { toast({ title: 'Explain the adjustment (at least 10 characters)', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/payroll/adjustments`, {
        time_entry_id: form.entry_id,
        adjusted_hours: hours,
        reason: form.reason.trim(),
      }, auth);
      toast({ title: 'Adjustment requested', description: 'A different admin must approve it.' });
      onDone();
      onClose();
    } catch (e) {
      toast({ title: 'Could not create adjustment', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-adjustment-modal">
        <DialogHeader>
          <DialogTitle>New payroll adjustment</DialogTitle>
          <DialogDescription>Correct the payable hours on a locked or paid entry. A second admin must approve.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Locked entry</Label>
            {entries === null ? (
              <Loader2 className="w-4 h-4 animate-spin mt-2" style={{ color: '#1E88E5' }} />
            ) : (
              <Select value={form.entry_id} onValueChange={(v) => setForm({ ...form, entry_id: v })}>
                <SelectTrigger data-testid="adjustment-entry-select"><SelectValue placeholder="Select an entry…" /></SelectTrigger>
                <SelectContent>
                  {entries.length === 0 && <SelectItem value="__none" disabled>No locked entries in the last 60 days</SelectItem>}
                  {entries.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.user_name} · {fmtDate(e.clock_in)} · {((e.regular_hours || 0) + (e.overtime_hours || 0)).toFixed(2)}h ({e.approval_status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs">Corrected total hours</Label>
            <Input type="number" step="0.25" min="0" value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              data-testid="adjustment-hours-input" />
          </div>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Textarea value={form.reason} rows={3}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Worker was paid for 8h but the site log shows 6.5h"
              data-testid="adjustment-reason-input" />
            <p className={`text-[11px] mt-1 text-right ${form.reason.trim().length >= 10 ? 'text-slate-500' : 'text-amber-400'}`} data-testid="adjustment-reason-count">
              {form.reason.trim().length}/10 characters minimum
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="adjustment-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || form.reason.trim().length < 10 || !form.entry_id} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="adjustment-submit-btn">
            {busy ? 'Creating…' : 'Request Adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdjustmentsTab = ({ token, currentUserId }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [adjustments, setAdjustments] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payroll/adjustments`, auth);
      setAdjustments(data.adjustments || []);
    } catch (e) { setAdjustments([]); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const approve = async (a) => {
    setBusyId(a.id);
    try {
      await axios.put(`${API}/payroll/adjustments/${a.id}/approve`, {}, auth);
      toast({ title: 'Adjustment approved' });
      load();
    } catch (e) {
      toast({ title: 'Could not approve', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const reject = async (reason) => {
    try {
      await axios.put(`${API}/payroll/adjustments/${rejecting.id}/reject`, { reason }, auth);
      toast({ title: 'Adjustment rejected' });
      setRejecting(null);
      load();
    } catch (e) {
      toast({ title: 'Could not reject', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const apply = async (a) => {
    setBusyId(a.id);
    try {
      await axios.post(`${API}/payroll/adjustments/${a.id}/apply`, {}, auth);
      toast({ title: 'Applied to next payroll' });
      load();
    } catch (e) {
      toast({ title: 'Could not apply', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  if (adjustments === null) {
    return <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="adjustments-tab">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowNew(true)}
          className="bg-[#1E88E5] hover:bg-blue-600 text-white font-semibold"
          data-testid="new-adjustment-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Adjustment
        </Button>
      </div>

      {adjustments.length === 0 ? (
        <div className="ht-card p-12 text-center">
          <p className="text-sm" style={{ color: '#64748B' }} data-testid="adjustments-empty">No payroll adjustments yet.</p>
        </div>
      ) : (
        <div className="ht-card p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="adjustments-table">
            <thead>
              <tr style={{ borderBottom: '1px solid #2D3748' }}>
                {['Worker', 'Original Hrs', 'Adjusted Hrs', 'Delta', 'Reason', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 font-medium text-xs" style={{ color: '#64748B' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => {
                const s = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
                const isRequester = a.requested_by === currentUserId;
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #1F2D3F' }} data-testid={`adjustment-row-${a.id}`}>
                    <td className="py-2.5 px-3 text-white font-medium whitespace-nowrap">{a.user_name}</td>
                    <td className="py-2.5 px-3" style={{ color: '#A0AEC0' }}>{a.original_hours.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-white">{a.adjusted_hours.toFixed(2)}</td>
                    <td className="py-2.5 px-3 font-semibold" style={{ color: a.adjustment_delta >= 0 ? '#00C896' : '#F87171' }}>
                      {a.adjustment_delta >= 0 ? '+' : ''}{a.adjustment_delta.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-xs max-w-[240px]" style={{ color: '#A0AEC0' }}>
                      <span className="line-clamp-2">{a.reason}</span>
                      {a.rejection_reason && <span className="block mt-0.5" style={{ color: '#F87171' }}>Rejected: {a.rejection_reason}</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                        style={{ background: s.bg, color: s.color }}
                        data-testid={`adjustment-status-${a.id}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        {a.status === 'pending' && (
                          <>
                            <button onClick={() => approve(a)} disabled={busyId === a.id || isRequester}
                              title={isRequester ? 'A different admin must approve your own request' : 'Approve'}
                              className="text-xs font-semibold px-2.5 py-1 rounded-lg disabled:opacity-35"
                              style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                              data-testid={`adj-approve-btn-${a.id}`}>
                              ✅ Approve
                            </button>
                            <button onClick={() => setRejecting(a)}
                              className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                              style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171' }}
                              data-testid={`adj-reject-btn-${a.id}`}>
                              ❌ Reject
                            </button>
                          </>
                        )}
                        {a.status === 'approved' && (
                          <button onClick={() => apply(a)} disabled={busyId === a.id}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                            style={{ background: 'rgba(30,136,229,0.15)', color: '#60A5FA' }}
                            data-testid={`adj-apply-btn-${a.id}`}>
                            Apply to Next Payroll
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewAdjustmentModal token={token} onDone={load} onClose={() => setShowNew(false)} />}
      {rejecting && (
        <NotesModal title={`Reject adjustment for ${rejecting.user_name}`}
          description="The requester will see this reason."
          placeholder="Why is this adjustment being rejected?"
          submitLabel="Reject" destructive
          onSubmit={reject} onClose={() => setRejecting(null)} testId="adj-reject-modal" />
      )}
    </div>
  );
};

export default AdjustmentsTab;
